import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { 
  MessageSquare, Send, Users, Activity, RefreshCw, Clock
} from "lucide-react";

export default function TeamCollaboration({ team, game, readOnly }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [activity, setActivity] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    if (team?.id) {
      fetchData();
      // Poll for new messages every 10 seconds
      const interval = setInterval(fetchMessages, 10000);
      return () => clearInterval(interval);
    }
  }, [team?.id]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchData = async () => {
    try {
      const [messagesRes, membersRes, activityRes] = await Promise.all([
        axios.get(`${API}/teams/${team.id}/messages`),
        axios.get(`${API}/teams/${team.id}/members`),
        axios.get(`${API}/teams/${team.id}/activity`)
      ]);
      setMessages(messagesRes.data);
      setMembers(membersRes.data);
      setActivity(activityRes.data);
    } catch (err) { console.error("TeamCollaboration:", err.message); } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/teams/${team.id}/messages`);
      setMessages(response.data);
    } catch (err) { console.error("TeamCollaboration:", err.message); }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const response = await axios.post(`${API}/teams/${team.id}/messages`, {
        content: newMessage
      });
      setMessages([...messages, response.data]);
      setNewMessage("");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-96 bg-slate-200 rounded-lg animate-pulse" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-heading font-semibold text-slate-900">{t("teamCollab.title")}</h3>
        <p className="text-sm text-slate-500">
          {t("teamCollab.chat")}
        </p>
      </div>

      <Tabs defaultValue="chat" className="space-y-6">
        <TabsList className="bg-slate-100 p-1">
          <TabsTrigger value="chat" className="gap-2" data-testid="tab-chat">
            <MessageSquare className="w-4 h-4" />
            {t("teamCollab.chat")}
          </TabsTrigger>
          <TabsTrigger value="members" className="gap-2" data-testid="tab-members">
            <Users className="w-4 h-4" />
            {t("teamCollab.members")} ({members.member_count || 0})
          </TabsTrigger>
          <TabsTrigger value="activity" className="gap-2" data-testid="tab-activity">
            <Activity className="w-4 h-4" />
            {t("teamCollab.activity")}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="chat" className="tab-content">
          <Card className="h-[500px] flex flex-col">
            <CardHeader className="border-b border-slate-100 py-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-sky-500" />
                  Team Chat
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={fetchMessages}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-y-auto p-4 space-y-4">
              {messages.length === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-500">
                  <div className="text-center">
                    <MessageSquare className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                    <p>No messages yet</p>
                    <p className="text-sm">Start the conversation!</p>
                  </div>
                </div>
              ) : (
                messages.map((message) => (
                  <MessageBubble 
                    key={message.id} 
                    message={message} 
                    isOwn={message.user_id === user?.id}
                  />
                ))
              )}
              <div ref={messagesEndRef} />
            </CardContent>
            <div className="border-t border-slate-100 p-4">
              {readOnly ? (
                <p className="text-sm text-slate-400 text-center py-1">Read-only mode — messages cannot be sent</p>
              ) : (
              <form onSubmit={handleSendMessage} className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={t("teamCollab.sendMessage")}
                  className="flex-1"
                  maxLength={1000}
                  data-testid="chat-input"
                />
                <Button type="submit" disabled={sending || !newMessage.trim()} data-testid="send-message-btn">
                  <Send className="w-4 h-4" />
                </Button>
              </form>
              )}
            </div>
          </Card>
        </TabsContent>

        <TabsContent value="members" className="tab-content">
          <Card>
            <CardHeader className="card-header-flight-deck">
              <CardTitle>{t("teamCollab.members")}</CardTitle>
              <CardDescription>{members.team_name}</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {members.members?.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No members yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {members.members?.map((member) => (
                    <div 
                      key={member.id}
                      className="flex items-center gap-4 p-4 bg-slate-50 rounded-lg"
                    >
                      <Avatar className="h-10 w-10">
                        <AvatarFallback className="bg-sky-100 text-sky-600">
                          {member.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <p className="font-medium text-slate-800">{member.name}</p>
                        <p className="text-sm text-slate-500">{member.email}</p>
                      </div>
                      {member.id === user?.id && (
                        <Badge variant="outline">You</Badge>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="tab-content">
          <Card>
            <CardHeader className="card-header-flight-deck">
              <CardTitle>{t("teamCollab.activity")}</CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {activity.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Activity className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                  <p>No activity yet</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {activity.map((item, index) => (
                    <ActivityItem key={index} item={item} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MessageBubble({ message, isOwn }) {
  const timeAgo = getTimeAgo(message.created_at);

  return (
    <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[80%] ${isOwn ? 'order-2' : 'order-1'}`}>
        {!isOwn && (
          <p className="text-xs text-slate-500 mb-1 ml-1">{message.user_name}</p>
        )}
        <div className={`px-4 py-2 rounded-2xl ${
          isOwn 
            ? 'bg-sky-500 text-white rounded-br-md' 
            : 'bg-slate-100 text-slate-800 rounded-bl-md'
        }`}>
          <p className="text-sm whitespace-pre-wrap break-words">{message.content}</p>
        </div>
        <p className={`text-xs text-slate-400 mt-1 ${isOwn ? 'text-right mr-1' : 'ml-1'}`}>
          {timeAgo}
        </p>
      </div>
    </div>
  );
}

function ActivityItem({ item }) {
  const timeAgo = getTimeAgo(item.timestamp);

  const icons = {
    decision: <Activity className="w-4 h-4 text-emerald-500" />,
    message: <MessageSquare className="w-4 h-4 text-sky-500" />
  };

  const bgColors = {
    decision: "bg-emerald-50 border-emerald-200",
    message: "bg-sky-50 border-sky-200"
  };

  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg border ${bgColors[item.type] || 'bg-slate-50 border-slate-200'}`}>
      <div className="mt-0.5">
        {icons[item.type] || <Clock className="w-4 h-4 text-slate-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-slate-700">{item.description}</p>
        <p className="text-xs text-slate-400 mt-1">{timeAgo}</p>
      </div>
    </div>
  );
}

function getTimeAgo(timestamp) {
  const now = new Date();
  const then = new Date(timestamp);
  const diffMs = now - then;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return then.toLocaleDateString();
}
